import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'coach' | 'athlete';
  athleteId?: string | null;
  googleDriveFolderLink?: string | null;
  country?: string | null;
  club?: string | null;
  gender?: 'male' | 'female' | null;
  authProvider?: 'local' | 'google' | 'both';
  dateOfBirth?: string | null;
  termsAcceptedAt?: string | null;
  cookiePreferences?: CookiePreferences & { consentedAt?: string | null };
  dashboardPreferences?: {
    topMetrics: string[];
    bottomSections: string[];
    shortcuts?: string[];
    selectedGraphType?: 'distance' | 'rating' | 'weight';
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<User>;
  signup: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    role: 'coach' | 'athlete',
    athleteId?: string,
    country?: string,
    club?: string,
    gender?: 'male' | 'female',
    dateOfBirth?: string,
    termsAccepted?: boolean,
    cookiePreferences?: CookiePreferences
  ) => Promise<User>;
  loginWithGoogle: () => void;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else if (response.status === 401 || response.status === 403) {
            // Only clear token on actual authentication errors (invalid/expired token)
            console.warn('Token is invalid or expired');
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          } else {
            // For other errors (500, network issues, etc.), keep the token but log the error
            console.error('Auth check failed with status:', response.status);
            // Don't log out on server errors - user might still be valid
          }
        } catch (error) {
          // Network errors or other exceptions - don't log out, just log the error
          console.error('Auth check error (network/server issue):', error);
          // If it's a network error (server not running), clear the user but keep token
          if (error instanceof TypeError && error.message.includes('fetch')) {
            // Network error - server might not be running
            console.warn('Server appears to be unavailable');
            setUser(null);
          }
          // Keep the token - might be a temporary server issue
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [token]);

  // Handle Google OAuth callback
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (token && window.location.pathname === '/auth/google/callback') {
        try {
          setToken(token);
          localStorage.setItem('token', token);
          
          // Fetch user profile
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            
            // Redirect based on role
            if (userData.role === 'coach') {
              window.location.href = '/coach/home';
            } else {
              window.location.href = '/jumper/home';
            }
          }
        } catch (error) {
          console.error('Google callback error:', error);
          setError('Failed to complete Google sign in');
        }
      }
    };

    handleGoogleCallback();
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    setIsLoading(true);
    setError(null);

    try {
      // Normalize email (lowercase and trim)
      const normalizedEmail = email.toLowerCase().trim();
      
      console.log('Attempting login for:', normalizedEmail);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail, password }),
      });

      console.log('Login response status:', response.status);

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = 'Login failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
          console.error('Login error response:', errorData);
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || `Server error (${response.status})`;
          console.error('Failed to parse error response:', e);
        }
        throw new Error(errorMessage);
      }

      // Parse JSON only if response is ok
      const data = await response.json();
      console.log('Login successful, user data received');

      if (!data.user || !data.token) {
        console.error('Invalid response structure:', data);
        throw new Error('Invalid response from server');
      }

      setUser(data.user);
      setToken(data.token);
      localStorage.setItem('token', data.token);
      console.log('User logged in successfully');
      return data.user;
    } catch (error) {
      let errorMessage = 'Login failed';
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        // Network error - server not running or connection issue
        errorMessage = 'Cannot connect to server. Please make sure the server is running on port 5001.';
        console.error('Network error - server may not be running:', error);
      } else if (error instanceof Error) {
        errorMessage = error.message;
        console.error('Login error:', error);
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    role: 'coach' | 'athlete',
    athleteId?: string,
    country?: string,
    club?: string,
    gender?: 'male' | 'female',
    dateOfBirth?: string,
    termsAccepted?: boolean,
    cookiePreferences?: CookiePreferences
  ): Promise<User> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
          role,
          athleteId,
          country,
          club,
          gender,
          dateOfBirth,
          termsAccepted,
          cookiePreferences,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Signup failed');
      }

      setUser(data.user);
      setToken(data.token);
      localStorage.setItem('token', data.token);
      return data.user;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Signup failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = () => {
    // Redirect to Google OAuth endpoint
    window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/google`;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const refreshProfile = async () => {
    const t = token ?? localStorage.getItem('token');
    if (!t) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/profile`, {
        headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (err) {
      console.error('Refresh profile error:', err);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    signup,
    loginWithGoogle,
    logout,
    refreshProfile,
    isLoading,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 